// Regional Anesthesia & Local Anesthetics Database
// Organized by class: amide local anesthetics, ester local anesthetics, adjuncts

export const regionalMedications = [
  // AMIDE LOCAL ANESTHETICS
  {
    name: 'Lidocaine',
    proprietaryName: 'Xylocaine',
    class: 'amide-local',
    categories: ['regional'],
    primaryCategory: 'regional',
    pKa: '7.9',
    proteinBinding: '65%',
    onset: 'Fast (2-5 min)',
    duration: 'Intermediate (60-120 min plain, 120-240 min with epi)',
    maxDose: {
      plain: '4.5 mg/kg (max 300 mg)',
      withEpi: '7 mg/kg (max 500 mg)',
      tumescent: 'Up to 35-55 mg/kg (with dilution + epi)'
    },
    concentrations: {
      infiltration: '0.5-1%',
      peripheral: '1-2%',
      epidural: '1.5-2%',
      spinal: '5% (hyperbaric)'
    },
    uses: [
      'Local infiltration',
      'Peripheral nerve blocks',
      'Epidural anesthesia',
      'Spinal anesthesia (5% hyperbaric)',
      'IV regional anesthesia (Bier block)',
      'Topical anesthesia (4% solution)'
    ],
    considerations: [
      'Most versatile local anesthetic',
      'Rapid onset due to low pKa (7.9)',
      'Good tissue penetration',
      'Effective as topical agent',
      'Can cause methemoglobinemia at high doses',
      'Antiarrhythmic properties (also cardiac toxic)',
      'Less motor block than bupivacaine',
      'Tachyphylaxis with repeated dosing',
      'CNS toxicity before cardiac toxicity'
    ],
    toxicity: {
      cns: 'Tinnitus, metallic taste, circumoral numbness, seizures',
      cardiac: 'Arrhythmias, hypotension, cardiac arrest',
      treatment: 'Lipid emulsion 20% (1.5 mL/kg bolus)'
    },
    bestFor: 'Rapid onset, intermediate duration procedures'
  },
  {
    name: 'Bupivacaine',
    proprietaryName: 'Marcaine',
    class: 'amide-local',
    categories: ['regional'],
    primaryCategory: 'regional',
    pKa: '8.1',
    proteinBinding: '95%',
    onset: 'Slow (10-20 min)',
    duration: 'Long (3-7 hrs plain, 5-10 hrs with epi)',
    maxDose: {
      plain: '2.5 mg/kg (max 175 mg)',
      withEpi: '3 mg/kg (max 225 mg)',
      spinal: 'Up to 20 mg'
    },
    concentrations: {
      infiltration: '0.25%',
      peripheral: '0.25-0.5%',
      epidural: '0.125-0.75%',
      spinal: '0.5-0.75% (hyperbaric or isobaric)'
    },
    uses: [
      'Long-duration peripheral nerve blocks',
      'Epidural anesthesia and analgesia',
      'Spinal anesthesia',
      'Local infiltration for prolonged procedures',
      'Continuous peripheral nerve catheters'
    ],
    considerations: [
      'Long duration due to high protein binding (95%)',
      'Slower onset than lidocaine (higher pKa)',
      'Significant motor block at higher concentrations',
      'HIGHLY cardiotoxic - avoid intravascular injection',
      'Cardiac toxicity may occur before CNS symptoms',
      'Resistant to resuscitation (use lipid emulsion early)',
      'Preferentially blocks sensory > motor at low concentrations',
      '0.5% = surgical, 0.25% = analgesia, 0.125% = labor epidural'
    ],
    toxicity: {
      cns: 'Seizures, loss of consciousness',
      cardiac: 'Wide-complex arrhythmias, refractory VF/VT, cardiovascular collapse',
      treatment: 'Lipid emulsion 20% (1.5 mL/kg bolus, repeat/infusion), prolonged CPR'
    },
    bestFor: 'Long-duration blocks, epidural labor analgesia'
  },
  {
    name: 'Ropivacaine',
    proprietaryName: 'Naropin',
    class: 'amide-local',
    categories: ['regional'],
    primaryCategory: 'regional',
    pKa: '8.1',
    proteinBinding: '94%',
    onset: 'Intermediate (10-15 min)',
    duration: 'Long (3-6 hrs plain, 5-8 hrs with epi)',
    maxDose: {
      plain: '3 mg/kg (max 225 mg)',
      withEpi: '3.5 mg/kg (max 250 mg)'
    },
    concentrations: {
      infiltration: '0.2%',
      peripheral: '0.5-0.75%',
      epidural: '0.2-0.75%',
      continuous: '0.1-0.2%'
    },
    uses: [
      'Peripheral nerve blocks',
      'Epidural anesthesia and analgesia',
      'Labor epidural analgesia',
      'Continuous peripheral nerve catheters',
      'Local infiltration'
    ],
    considerations: [
      'S-enantiomer (pure left-handed isomer)',
      'Less cardiotoxic than bupivacaine (40% less)',
      'Less motor block than equivalent dose of bupivacaine',
      'Preferred over bupivacaine for labor epidurals',
      'Intrinsic vasoconstrictor properties',
      'Lower lipid solubility than bupivacaine',
      'Better sensory/motor separation',
      'May allow ambulation during labor at low concentrations'
    ],
    toxicity: {
      cns: 'Similar to bupivacaine but less severe',
      cardiac: 'Less cardiotoxic than bupivacaine, easier to resuscitate',
      treatment: 'Lipid emulsion, supportive care'
    },
    bestFor: 'Epidural labor analgesia, when motor-sparing desired'
  },
  {
    name: 'Mepivacaine',
    proprietaryName: 'Carbocaine',
    class: 'amide-local',
    categories: ['regional'],
    primaryCategory: 'regional',
    pKa: '7.6',
    proteinBinding: '75%',
    onset: 'Fast (3-5 min)',
    duration: 'Intermediate (2-3 hrs plain, 3-5 hrs with epi)',
    maxDose: {
      plain: '5 mg/kg (max 400 mg)',
      withEpi: '7 mg/kg (max 550 mg)'
    },
    concentrations: {
      infiltration: '0.5-1%',
      peripheral: '1-1.5%',
      epidural: '1-2%'
    },
    uses: [
      'Dental procedures',
      'Peripheral nerve blocks',
      'Epidural anesthesia',
      'Local infiltration'
    ],
    considerations: [
      'Rapid onset (lowest pKa of amides)',
      'Intrinsic vasoconstrictor properties (less need for epi)',
      'Less vasodilation than lidocaine',
      'Intermediate duration',
      'Minimal placental transfer',
      'Metabolized slower in neonates - avoid in OB',
      'Good alternative to lidocaine for dental/oral surgery',
      'Less tachyphylaxis than lidocaine'
    ],
    toxicity: {
      cns: 'Similar to lidocaine',
      cardiac: 'Less cardiotoxic than bupivacaine',
      treatment: 'Supportive care, lipid emulsion if severe'
    },
    bestFor: 'Dental blocks, peripheral nerve blocks when epi contraindicated'
  },
  {
    name: 'Levobupivacaine',
    proprietaryName: 'Chirocaine',
    class: 'amide-local',
    categories: ['regional'],
    primaryCategory: 'regional',
    pKa: '8.1',
    proteinBinding: '97%',
    onset: 'Slow (10-20 min)',
    duration: 'Long (similar to bupivacaine)',
    maxDose: {
      plain: '2.5 mg/kg (max 150 mg)',
      withEpi: '3 mg/kg'
    },
    concentrations: {
      infiltration: '0.25%',
      peripheral: '0.25-0.5%',
      epidural: '0.125-0.75%'
    },
    uses: [
      'Peripheral nerve blocks',
      'Epidural anesthesia',
      'Spinal anesthesia',
      'Continuous nerve catheters'
    ],
    considerations: [
      'S-enantiomer of bupivacaine (pure left isomer)',
      'Less cardiotoxic than racemic bupivacaine',
      'Similar efficacy to bupivacaine',
      'Similar motor block to bupivacaine',
      'More expensive than bupivacaine',
      'Not available in US (available in Europe/Asia)',
      'Better safety profile in accidental IV injection'
    ],
    toxicity: {
      cns: 'Similar to bupivacaine',
      cardiac: 'Less cardiotoxic than racemic bupivacaine',
      treatment: 'Lipid emulsion, supportive care'
    },
    bestFor: 'Alternative to bupivacaine when available (better safety)'
  },

  // ESTER LOCAL ANESTHETICS
  {
    name: 'Chloroprocaine',
    proprietaryName: 'Nesacaine',
    class: 'ester-local',
    categories: ['regional'],
    primaryCategory: 'regional',
    pKa: '8.7',
    proteinBinding: 'Low',
    onset: 'Very fast (3-5 min)',
    duration: 'Short (30-60 min)',
    maxDose: {
      plain: '11 mg/kg (max 800 mg)',
      withEpi: '14 mg/kg (max 1000 mg)'
    },
    concentrations: {
      infiltration: '1%',
      epidural: '2-3%',
      peripheral: '1-2%'
    },
    uses: [
      'Epidural anesthesia for cesarean delivery',
      'Short surgical procedures',
      'Local infiltration',
      'Peripheral nerve blocks'
    ],
    considerations: [
      'Ester local anesthetic - metabolized by plasma cholinesterase',
      'Very rapid onset and offset',
      'Safest local anesthetic in pregnancy (rapid metabolism)',
      'Lowest systemic toxicity',
      'May antagonize epidural opioid analgesia',
      'Old formulations caused neurotoxicity (bisulfite preservative)',
      'Modern preservative-free formulation is safe',
      'Useful when short duration needed',
      'Good for failed spinal requiring epidural rescue'
    ],
    toxicity: {
      cns: 'Rare due to rapid metabolism',
      cardiac: 'Very low risk',
      allergic: 'Higher risk than amides (PABA metabolite)',
      treatment: 'Supportive care'
    },
    bestFor: 'Rapid epidural for cesarean, short procedures'
  },

  // ADJUNCTS
  {
    name: 'Epinephrine (Local Anesthetic Adjunct)',
    proprietaryName: 'Adrenaline',
    class: 'adjunct',
    categories: ['regional'],
    primaryCategory: 'regional',
    onset: '5-10 minutes',
    duration: 'Prolongs block by 50-100%',
    dosing: {
      concentration: '1:200,000 (5 mcg/mL) or 1:400,000 (2.5 mcg/mL)',
      maxDose: '200-250 mcg total',
      units: 'mcg'
    },
    uses: [
      'Prolong duration of local anesthetics',
      'Decrease systemic absorption',
      'Identify intravascular injection (test dose)',
      'Improve block quality'
    ],
    considerations: [
      'Vasoconstriction reduces absorption and prolongs block',
      'Best with intermediate-duration agents (lidocaine, mepivacaine)',
      'Less effect on long-acting agents (bupivacaine, ropivacaine)',
      'CONTRAINDICATED in end-arterial areas (fingers, toes, penis, nose)',
      'May delay detection of intravascular injection',
      'Test dose: 15 mcg (HR ↑ >20 bpm suggests IV)',
      'Typically 1:200,000 concentration (5 mcg/mL)',
      'Benefits reduced in highly vascularized areas'
    ],
    bestFor: 'Prolonging infiltration and peripheral blocks'
  },
  {
    name: 'Dexmedetomidine (Perineural)',
    proprietaryName: 'Precedex',
    class: 'adjunct',
    categories: ['regional'],
    primaryCategory: 'regional',
    onset: '10-20 minutes',
    duration: 'Prolongs block by 2-4 hours',
    dosing: {
      perineural: '0.5-1 mcg/kg (typically 50-100 mcg total)',
      units: 'mcg'
    },
    uses: [
      'Prolong peripheral nerve blocks',
      'Improve block quality',
      'Reduce opioid requirements'
    ],
    considerations: [
      'α2-agonist - prolongs sensory and motor block',
      'Off-label for perineural use (not FDA approved)',
      'May cause bradycardia and hypotension',
      'Less sedation than IV route at perineural doses',
      'Appears safe for perineural use (animal + human data)',
      'May be superior to clonidine',
      'Dose-dependent block prolongation',
      'Consider for same-day surgery when prolonged analgesia desired'
    ],
    bestFor: 'Prolonging peripheral nerve blocks for extended analgesia'
  },
  {
    name: 'Dexamethasone (Perineural)',
    proprietaryName: 'Decadron',
    class: 'adjunct',
    categories: ['regional'],
    primaryCategory: 'regional',
    onset: '30-60 minutes',
    duration: 'Prolongs block by 4-8 hours',
    dosing: {
      perineural: '4-8 mg (typically 4 mg)',
      iv: '4-10 mg IV (similar effect to perineural)',
      units: 'mg'
    },
    uses: [
      'Prolong peripheral nerve blocks',
      'Reduce inflammation',
      'Improve postoperative analgesia',
      'Antiemetic effect'
    ],
    considerations: [
      'Steroid - anti-inflammatory mechanism for prolongation',
      'Perineural vs IV shows similar block prolongation',
      'IV route may be preferable (safer, same benefit)',
      'Prolongs both sensory and motor block',
      'May increase glucose in diabetics',
      'Single dose has minimal systemic effects',
      'Does not cause neural damage in studies',
      '4 mg appears as effective as higher doses',
      'Consider especially for ambulatory surgery'
    ],
    bestFor: 'Prolonging blocks for outpatient surgery (IV or perineural)'
  },
  {
    name: 'Sodium Bicarbonate',
    proprietaryName: 'Bicarb',
    class: 'adjunct',
    categories: ['regional'],
    primaryCategory: 'regional',
    onset: 'Speeds onset by 25-50%',
    duration: 'No significant change',
    dosing: {
      ratio: '1 mEq per 10 mL of local anesthetic',
      maxRatio: 'Do not exceed 1:10 ratio (precipitation)',
      units: 'mEq'
    },
    uses: [
      'Speed onset of local anesthetics',
      'Reduce pain on injection (buffering)',
      'Improve block quality'
    ],
    considerations: [
      'Increases pH of local anesthetic solution',
      'More unionized form crosses membranes faster',
      'Most effective with lidocaine and mepivacaine',
      'Less effective with bupivacaine (may precipitate)',
      'Reduces pain on injection (infiltration)',
      'Use fresh mixture (loses potency over time)',
      'Standard ratio: 1 mEq bicarb per 10 mL LA',
      'May precipitate if too much added',
      'Most useful for epidurals and infiltration'
    ],
    bestFor: 'Speeding onset of epidural/infiltration with lidocaine'
  },
  {
    name: 'Clonidine (Perineural)',
    proprietaryName: 'Duraclon',
    class: 'adjunct',
    categories: ['regional'],
    primaryCategory: 'regional',
    onset: '15-30 minutes',
    duration: 'Prolongs block by 2-3 hours',
    dosing: {
      perineural: '0.5-1.5 mcg/kg (typically 50-150 mcg)',
      epidural: '1-2 mcg/kg',
      units: 'mcg'
    },
    uses: [
      'Prolong peripheral nerve blocks',
      'Neuraxial analgesia',
      'Chronic pain management'
    ],
    considerations: [
      'α2-agonist - prolongs block via peripheral and central mechanisms',
      'May cause hypotension and bradycardia',
      'Sedation common',
      'Prolongs both sensory and motor block',
      'May improve block quality',
      'FDA approved for epidural use',
      'Off-label for perineural use',
      'Dexmedetomidine may be preferred (more selective α2)'
    ],
    bestFor: 'Alternative to dexmedetomidine for block prolongation'
  }
];

// Organized exports by class
export const amideLocalAnesthetics = regionalMedications.filter(med => med.class === 'amide-local');
export const esterLocalAnesthetics = regionalMedications.filter(med => med.class === 'ester-local');
export const regionalAdjuncts = regionalMedications.filter(med => med.class === 'adjunct');

export default regionalMedications;
